// GOV13 Step 5 Slice 4 — the SINGLE frontend consumer of the backend purchase-mode envelope. The frontend NEVER
// infers purchase eligibility itself (not from price presence, product name, "Enterprise" text, current plan,
// local role, catalogue order, or legacy request status). It renders strictly from resolve_purchase_mode's
// envelope. This module is pure: mode constants, HTTP-outcome mapping, the server-idempotency-aligned submission
// key, role gating, and canonical label formatting. No fetch, no state, no optimistic fulfilment.

export const PURCHASE_MODES = {
  DIRECT_CHECKOUT: 'DIRECT_CHECKOUT',
  REQUEST_REQUIRED: 'REQUEST_REQUIRED',
  ALREADY_ACTIVE: 'ALREADY_ACTIVE',
  INCLUDED_IN_PLAN: 'INCLUDED_IN_PLAN',
  INELIGIBLE: 'INELIGIBLE',
  UNAVAILABLE: 'UNAVAILABLE',
};

// EXACT Business billing-admin roles that may see an ENABLED commercial mutation control. Backend remains the
// authority (this is convenience hiding only). super_admin must NEVER pass via the substring "admin".
const PURCHASE_ROLES = new Set(['admin', 'administrator']);
export function canPurchase(role) {
  return PURCHASE_ROLES.has(String(role || '').trim().toLowerCase());
}

// Submission-guard key — mirrors the backend purchase-attempt idempotency scope (product + version + quantity).
// The client NEVER mints a permanent idempotency key; this only disables the one in-flight control.
export function submissionKey(env) {
  if (!env) return '';
  return `${env.product_type}:${env.product_key}:${env.product_version}:${env.quantity ?? 1}`;
}

// Truthful HTTP-outcome mapping. A commercial POLICY (already-active / included / request-required / ineligible)
// is NEVER rendered as a provider outage or a payment failure; a 403 is NEVER a 503.
export function mapPurchaseHttpError(status, detailCode, detailMessage) {
  const code = detailCode || '';
  if (status === 401) return { kind: 'session', message: 'Your session has expired. Please sign in again.' };
  if (status === 403) return { kind: 'forbidden', message: 'You are not authorized to make this change.' };
  if (status === 409 && code === 'PRODUCT_ALREADY_ACTIVE') return { kind: 'already_active', message: 'Already active.' };
  if (status === 409 && code === 'PRODUCT_INCLUDED_IN_PLAN') return { kind: 'included', message: 'Included in your plan.' };
  if (status === 409) return { kind: 'conflict', message: detailMessage || 'This product is already in place.' };
  if (status === 422 && code === 'PURCHASE_REQUEST_REQUIRED') return { kind: 'request_required', message: 'This product requires review before purchase.' };
  if (status === 422) return { kind: 'ineligible', message: detailMessage || 'This product is not available for your account.' };
  if (status === 503) return { kind: 'provider', message: 'The payment provider is temporarily unavailable. Please try again shortly.' };
  return { kind: 'error', message: detailMessage || 'Something went wrong. Please retry.' };
}

// Canonical label formatting straight from the envelope (never recomputed from a name/plan).
export function priceLabel(env) {
  if (!env || env.price_cents == null) return null;
  const cur = (env.currency || 'USD').toUpperCase();
  const amount = (env.price_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${cur} ${amount}`;
}
export function cadenceLabel(env) {
  if (!env) return '';
  return env.cadence === 'recurring' ? '/mo' : ' one-time';
}
export function taxNote(env) {
  if (!env || env.price_cents == null) return '';
  return (env.tax_behavior === 'exclusive') ? 'Tax (GST 5%) added at checkout' : 'Tax included';
}

// The one place that decides which control a product card renders. Returns a small descriptor the presentation
// component maps to elements — no business inference, just the server mode.
export function purchaseControlFor(env, role) {
  const mode = env && env.purchase_mode;
  const allowed = canPurchase(role);
  switch (mode) {
    case PURCHASE_MODES.DIRECT_CHECKOUT:
      return { control: 'purchase', enabled: allowed, reason: null };
    case PURCHASE_MODES.REQUEST_REQUIRED:
      return { control: 'request_review', enabled: allowed, reason: env.reason_message };
    case PURCHASE_MODES.ALREADY_ACTIVE:
      return { control: 'none', badge: 'Already active', reason: null };
    case PURCHASE_MODES.INCLUDED_IN_PLAN:
      return { control: 'none', badge: 'Included in plan', reason: null };
    case PURCHASE_MODES.INELIGIBLE:
      return { control: 'none', badge: null, reason: env.reason_message || 'Not available for your account' };
    case PURCHASE_MODES.UNAVAILABLE:
    default:
      return { control: 'none', badge: null, reason: (env && env.reason_message) || 'Currently unavailable' };
  }
}
